import { LOCAL_STORAGE_BASE_KEYS } from "@/types/v2";

/**
 * A class for managing local storage keys based on configuration.
 */
class ConfigurableLocalStorage {
  allowedKeys: string[];
  restrictedKeys: string[];
  /**
   * @typedef {object} ConfigurableLocalStorageConfig - Configuration object.
   * @property {string[]} allowedKeys - List of allowed keys.
   * @property {string[]} restrictedKeys - List of restricted keys.
   */

  /**
   * @typedef {object} LocalStorageItem - Object representing a local storage item.
   * @property {string} key - The key of the local storage item.
   * @property {*} value - The value of the local storage item.
   */

  /**
   * Creates an instance of ConfigurableLocalStorage.
   * @param {ConfigurableLocalStorageConfig} config - Configuration object.
   * @param config.allowedKeys
   * @param config.restrictedKeys
   */
  constructor(config: { allowedKeys: string[]; restrictedKeys: string[] }) {
    this.allowedKeys = config.allowedKeys || [];
    this.restrictedKeys = config.restrictedKeys || [];
  }

  /**
   * Retrieves the value of a local storage item by key.
   * @param {string} key - The key of the local storage item.
   * @param {string} [shopId] - The shop ID.
   * @returns {*} - The value of the local storage item.
   */
  getItem(key: string, shopId?: string) {
    if (this.isKeyAllowed(key)) {
      return localStorage.getItem(this.generateKey(key, shopId));
    } else {
      console.error(`Key "${key}" is not allowed.`);
      return null;
    }
  }

  /**
   * Sets the value of a local storage item.
   * @param {string} key - The key of the local storage item.
   * @param {string | number | undefined | null} value - The value to be set.
   * @param {string} [shopId] - The shop ID.
   */
  setItem(
    key: string,
    value: string | number | undefined | null,
    shopId?: string
  ) {
    if (this.isKeyAllowed(key)) {
      localStorage.setItem(this.generateKey(key, shopId), `${value ?? ""}`);
    } else {
      console.error(`Key "${key}" is not allowed.`);
    }
  }

  /**
   * Generates a key by combining the provided key with an optional shop ID.
   * @param {string} key - The base key to be used for generating the new key.
   * @param {string} [shopId] - An optional shop ID to append to the base key.
   * @returns {string} Returns the generated key. If shopId is provided, it returns a combination of the key and shopId separated by a hyphen; otherwise, it returns the base key.
   */
  generateKey(key: string, shopId?: string) {
    if (shopId) {
      return `${key}-${shopId}`;
    }
    return key;
  }

  /**
   * Retrieves all local storage items based on the allowed and restricted keys.
   * @returns {LocalStorageItem[]} - Array of local storage items.
   */
  getAllItems() {
    const items = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key !== null && this.isKeyAllowed(key)) {
        items.push({ key, value: this.getItem(key) });
      }
    }
    return items;
  }

  /**
   * Checks if a given key is allowed based on the configuration.
   * @param {string} key - The key to be checked.
   * @returns {boolean} - True if the key is allowed, false otherwise.
   */
  isKeyAllowed(key: string) {
    return (
      (this.allowedKeys.length === 0 || this.allowedKeys.includes(key)) &&
      !this.restrictedKeys.includes(key)
    );
  }
}

const localStorageConfig = {
  allowedKeys: Object.keys(LOCAL_STORAGE_BASE_KEYS).map(
    (key: string) =>
      LOCAL_STORAGE_BASE_KEYS[key as keyof typeof LOCAL_STORAGE_BASE_KEYS]
  ),
  restrictedKeys: [],
};

export const localStorageManager = new ConfigurableLocalStorage(
  localStorageConfig
);
